'use client';

import { hashKey } from '@zg-rentals/react-query5';
import { usePathname, useSearchParams } from 'next/navigation';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import type { SearchListingsRequest } from '@/src/lib/api';
import { searchKeys } from '@/src/lib/hooks/query/queryKeys';
import { emitTrackViewSrpEvent, emitTrackZeroResultSearchEvent } from './hotpads/events';

type TrackedLiveSearch = {
  requestKey: string;
  sequence: number;
};

type PendingCanonicalRemount = TrackedLiveSearch & {
  sourceUrl: string;
  targetUrl: string;
};

type SrpPageViewCoordinatorValue = {
  registerCanonicalRemount: (requestKey: string, targetUrl: string) => void;
  trackLiveSearch: (requestKey: string, isAgentic: boolean) => void;
  trackMount: (isAgentic: boolean) => void;
  trackZeroResultSearch: (requestKey: string, isZeroResult: boolean, isAgentic: boolean) => void;
};

const SrpPageViewCoordinatorContext = createContext<SrpPageViewCoordinatorValue | null>(null);

function getCurrentUrl(): string {
  return typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`;
}

function normalizeUrl(url: string): string {
  if (typeof window === 'undefined') return url;
  const parsedUrl = new URL(url, window.location.href);
  return `${parsedUrl.pathname}${parsedUrl.search}`;
}

export function getSrpPageViewRequestKey(request: SearchListingsRequest): string {
  return hashKey(searchKeys.listings(request));
}

export function SrpPageViewCoordinatorProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const nextSequenceRef = useRef(0);
  const trackedLiveSearchRef = useRef<TrackedLiveSearch | null>(null);
  const pendingCanonicalRemountRef = useRef<PendingCanonicalRemount | null>(null);
  // Shell-scoped, because the slug morph remounts the page under this provider:
  // component-local refs would forget the search and count it a second time.
  const lastZeroResultRequestKeyRef = useRef<string | null>(null);
  const emittedZeroResultRequestKeyRef = useRef<string | null>(null);

  const trackMount = useCallback((isAgentic: boolean) => {
    const pendingCanonicalRemount = pendingCanonicalRemountRef.current;
    const trackedLiveSearch = trackedLiveSearchRef.current;
    const shouldConsumeCanonicalRemount =
      pendingCanonicalRemount?.targetUrl === getCurrentUrl() &&
      pendingCanonicalRemount.sequence === trackedLiveSearch?.sequence;

    pendingCanonicalRemountRef.current = null;
    trackedLiveSearchRef.current = null;

    if (shouldConsumeCanonicalRemount) return;
    // An ordinary mount is a new page view, so the search it lands on counts
    // again — the SRP re-entered from an HDP is the same search, but it is a
    // second view of it, and the zero-result rate divides by views.
    lastZeroResultRequestKeyRef.current = null;
    emittedZeroResultRequestKeyRef.current = null;
    emitTrackViewSrpEvent({ isAgentic });
  }, []);

  const trackLiveSearch = useCallback((requestKey: string, isAgentic: boolean) => {
    nextSequenceRef.current += 1;
    trackedLiveSearchRef.current = { requestKey, sequence: nextSequenceRef.current };
    pendingCanonicalRemountRef.current = null;
    emitTrackViewSrpEvent({ isAgentic });
  }, []);

  // Two keys, because "already counted" is per search, not for all time: going
  // back to an earlier empty search is a new search that returned nothing, and
  // collapsing it into the first one would undercount the rate.
  const trackZeroResultSearch = useCallback((requestKey: string, isZeroResult: boolean, isAgentic: boolean) => {
    // An agentic SRP view is counted as AGENTIC_VIEW_SRP, never VIEW_SRP, so an
    // agentic zero result would land in the numerator with nothing added to the
    // denominator. Both sides leave these sessions out.
    if (isAgentic) return;

    if (requestKey !== lastZeroResultRequestKeyRef.current) {
      lastZeroResultRequestKeyRef.current = requestKey;
      emittedZeroResultRequestKeyRef.current = null;
    }

    // A search settles empty after a spell in flight, so this runs again for the
    // same key — and a refetch of that same search must not count twice.
    if (!isZeroResult) return;
    if (emittedZeroResultRequestKeyRef.current === requestKey) return;

    emittedZeroResultRequestKeyRef.current = requestKey;
    emitTrackZeroResultSearchEvent();
  }, []);

  const registerCanonicalRemount = useCallback((requestKey: string, targetUrl: string) => {
    const trackedLiveSearch = trackedLiveSearchRef.current;
    const sourceUrl = getCurrentUrl();
    const normalizedTargetUrl = normalizeUrl(targetUrl);

    if (!trackedLiveSearch || trackedLiveSearch.requestKey !== requestKey || sourceUrl === normalizedTargetUrl) {
      pendingCanonicalRemountRef.current = null;
      return;
    }

    pendingCanonicalRemountRef.current = {
      ...trackedLiveSearch,
      sourceUrl,
      targetUrl: normalizedTargetUrl,
    };
  }, []);

  useEffect(() => {
    const pendingCanonicalRemount = pendingCanonicalRemountRef.current;
    if (!pendingCanonicalRemount) return;

    const currentUrl = getCurrentUrl();
    if (currentUrl !== pendingCanonicalRemount.sourceUrl && currentUrl !== pendingCanonicalRemount.targetUrl) {
      pendingCanonicalRemountRef.current = null;
      trackedLiveSearchRef.current = null;
      return;
    }

    if (currentUrl !== pendingCanonicalRemount.targetUrl) return;
    queueMicrotask(() => {
      if (pendingCanonicalRemountRef.current?.sequence !== pendingCanonicalRemount.sequence) return;
      pendingCanonicalRemountRef.current = null;
      trackedLiveSearchRef.current = null;
    });
  }, [pathname, search]);

  const value = useMemo(
    () => ({ registerCanonicalRemount, trackLiveSearch, trackMount, trackZeroResultSearch }),
    [registerCanonicalRemount, trackLiveSearch, trackMount, trackZeroResultSearch],
  );

  return <SrpPageViewCoordinatorContext.Provider value={value}>{children}</SrpPageViewCoordinatorContext.Provider>;
}

export function useSrpPageViewCoordinator(): SrpPageViewCoordinatorValue {
  const coordinator = useContext(SrpPageViewCoordinatorContext);
  if (!coordinator) {
    throw new Error('useSrpPageViewCoordinator must be used within SrpPageViewCoordinatorProvider');
  }
  return coordinator;
}
